import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Typography, Box, IconButton, Tooltip, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, Collapse, Checkbox, ListItemText, OutlinedInput,
  TablePagination
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import { employeeService, departmentService, positionService, seniorityLevelService } from '../services/api';
import { getEmployeeFullName } from '../utils/names';
import { IN_STAFF_STATUSES } from '../utils/staffStatus';
import SkeletonBlock from './ui/SkeletonBlock';
import EmptyState from './ui/EmptyState';
import { useConfirm } from './Common/ConfirmDialog';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// All employee statuses
const ALL_STATUSES = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Terminated', label: 'Terminated' },
  { value: 'OnLeave', label: 'On Leave' },
  { value: 'OnMaternityLeave', label: 'On Maternity/Paternity Leave' },
  { value: 'OnSickLeave', label: 'On Sick Leave' },
  { value: 'Probation', label: 'Probation' },
  { value: 'Suspended', label: 'Suspended' },
  { value: 'NoticePeriod', label: 'Notice Period' },
  { value: 'Retired', label: 'Retired' }
];

// Tenure range options
const TENURE_RANGES = [
  { value: 'all', label: 'All' },
  { value: 'less1', label: '< 1 year' },
  { value: '1to2', label: '1-2 years' },
  { value: '2to5', label: '2-5 years' },
  { value: 'more5', label: '5+ years' }
];

const EmployeeList = ({ embedded = false, onRowClick = null, hideActions = false }) => {
  const confirm = useConfirm();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Filter options
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [seniorityLevels, setSeniorityLevels] = useState([]);

  // Filter values (arrays for multi-select)
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedSeniorityLevels, setSelectedSeniorityLevels] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([...IN_STAFF_STATUSES]);   // STAFF-S1
  const [selectedTenureRanges, setSelectedTenureRanges] = useState([]);
  const [hireDateFrom, setHireDateFrom] = useState(null);
  const [hireDateTo, setHireDateTo] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    loadEmployees();
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      const [depts, pos, levels] = await Promise.all([
        departmentService.getAll(),
        positionService.getAll(),
        seniorityLevelService.getAll()
      ]);
      setDepartments(Array.isArray(depts) ? depts : []);
      setPositions(Array.isArray(pos) ? pos : []);
      setSeniorityLevels(Array.isArray(levels) ? levels : []);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await employeeService.getAll();
      setEmployees(response || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (await confirm({ message: 'Are you sure you want to delete this employee?', danger: true, confirmLabel: 'Delete' })) {
      try {
        await employeeService.delete(id);
        loadEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
      }
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedDepartments([]);
    setSelectedPositions([]);
    setSelectedSeniorityLevels([]);
    setSelectedStatuses([...IN_STAFF_STATUSES]);
    setSelectedTenureRanges([]);
    setHireDateFrom(null);
    setHireDateTo(null);
    setPage(0);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedDepartments, selectedPositions, selectedSeniorityLevels,
      selectedStatuses, selectedTenureRanges, hireDateFrom, hireDateTo]);

  // Check if the default filter is on (STAFF-S1: everybody in staff)
  const DEFAULT_STATUSES = IN_STAFF_STATUSES;
  const isDefaultFilter = selectedStatuses.length === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every(s => selectedStatuses.includes(s));

  const hasActiveFilters = useMemo(() => {
    return searchTerm ||
           selectedDepartments.length > 0 ||
           selectedPositions.length > 0 ||
           selectedSeniorityLevels.length > 0 ||
           !isDefaultFilter ||
           selectedTenureRanges.length > 0 ||
           hireDateFrom || hireDateTo;
  }, [searchTerm, selectedDepartments, selectedPositions, selectedSeniorityLevels,
      isDefaultFilter, selectedTenureRanges, hireDateFrom, hireDateTo]);

  // Calculate tenure years from hire date
  const getTenureYears = (hireDate) => {
    if (!hireDate) return 0;
    const hire = new Date(hireDate);
    const today = new Date();
    return (today - hire) / (365.25 * 24 * 60 * 60 * 1000);
  };

  // Format tenure as readable string
  const formatTenure = (hireDate) => {
    if (!hireDate) return 'N/A';
    const hire = new Date(hireDate);
    const today = new Date();

    let years = today.getFullYear() - hire.getFullYear();
    let months = today.getMonth() - hire.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    // Adjust for day of month
    if (today.getDate() < hire.getDate()) {
      months--;
      if (months < 0) {
        years--;
        months += 12;
      }
    }

    if (years > 0 && months > 0) {
      return `${years} г. ${months} м.`;
    } else if (years > 0) {
      return `${years} г.`;
    } else if (months > 0) {
      return `${months} м.`;
    } else {
      return '< 1 м.';
    }
  };

  // Helper to check if tenure matches any selected range
  const matchesTenureRange = (hireDate, ranges) => {
    if (ranges.length === 0) return true;
    const years = getTenureYears(hireDate);
    return ranges.some(range => {
      switch (range) {
        case 'less1': return years < 1;
        case '1to2': return years >= 1 && years < 2;
        case '2to5': return years >= 2 && years < 5;
        case 'more5': return years >= 5;
        default: return true;
      }
    });
  };

  // Apply all filters
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      // Text search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const fullName = getEmployeeFullName(emp) || '';
        const matches =
          fullName.toLowerCase().includes(term) ||
          (emp.email || '').toLowerCase().includes(term) ||
          (emp.department?.name || '').toLowerCase().includes(term) ||
          (emp.position?.title || '').toLowerCase().includes(term);
        if (!matches) return false;
      }

      // Status filter (multi-select)
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(emp.status)) {
        return false;
      }

      // Department filter (multi-select)
      if (selectedDepartments.length > 0 && !selectedDepartments.includes(emp.departmentId)) {
        return false;
      }

      // Position filter (multi-select)
      if (selectedPositions.length > 0 && !selectedPositions.includes(emp.positionId)) {
        return false;
      }

      // Seniority Level filter (multi-select)
      if (selectedSeniorityLevels.length > 0 && !selectedSeniorityLevels.includes(emp.seniorityLevelId)) {
        return false;
      }

      // Tenure range filter (multi-select)
      if (!matchesTenureRange(emp.hireDate, selectedTenureRanges)) {
        return false;
      }

      // Hire date range filter
      if (hireDateFrom || hireDateTo) {
        const hireDate = emp.hireDate ? new Date(emp.hireDate) : null;
        if (!hireDate) return false;
        if (hireDateFrom && hireDate < hireDateFrom) return false;
        if (hireDateTo && hireDate > hireDateTo) return false;
      }

      return true;
    });
  }, [employees, searchTerm, selectedDepartments, selectedPositions,
      selectedSeniorityLevels, selectedStatuses, selectedTenureRanges, hireDateFrom, hireDateTo]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Probation': return 'info';
      case 'OnLeave':
      case 'OnMaternityLeave':
      case 'OnSickLeave': return 'warning';
      case 'Suspended':
      case 'NoticePeriod': return 'error';
      case 'Terminated':
      case 'Retired':
      case 'Inactive': return 'default';
      default: return 'default';
    }
  };

  if (loading) return <SkeletonBlock rows={6} />;

  if (!loading && employees.length === 0) {
    return (
      <EmptyState title="No employees" description="There are no employees yet." actionLabel="Add Employee" onAction={()=> navigate('/admin/employee-case')} />
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: embedded ? 0 : 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header - hide in embedded mode */}
        {!embedded && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h5">Employees</Typography>
              <Chip
                label={selectedStatuses.length === 0
                  ? 'All Statuses'
                  : isDefaultFilter
                    ? 'In staff'
                    : selectedStatuses.length === 1
                    ? ALL_STATUSES.find(s => s.value === selectedStatuses[0])?.label
                    : `${selectedStatuses.length} statuses`}
                color={selectedStatuses.length === 0 ? 'secondary' : 'primary'}
                size="small"
                variant={selectedStatuses.length === 0 ? 'outlined' : 'filled'}
              />
              <Chip
                label={`${filteredEmployees.length} / ${employees.length}`}
                color="default"
                size="small"
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  color={hasActiveFilters ? "primary" : "default"}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/admin/employee-case')}
                size="small"
              >
                Add Employee
              </Button>
            </Box>
          </Box>
        )}

        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, department, or position..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Collapsible Filters */}
        <Collapse in={showFilters}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 2,
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1
          }}>
            {/* Status Filter (Multi-select) */}
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={selectedStatuses}
                onChange={(e) => setSelectedStatuses(e.target.value)}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected) => selected.length === 0
                  ? 'All'
                  : selected.map(v => ALL_STATUSES.find(s => s.value === v)?.label || v).join(', ')}
                MenuProps={MenuProps}
              >
                {ALL_STATUSES.map(s => (
                  <MenuItem key={s.value} value={s.value}>
                    <Checkbox checked={selectedStatuses.includes(s.value)} size="small" />
                    <ListItemText primary={s.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Department Filter (Multi-select) */}
            <FormControl size="small" fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                multiple
                value={selectedDepartments}
                onChange={(e) => setSelectedDepartments(e.target.value)}
                input={<OutlinedInput label="Department" />}
                renderValue={(selected) => selected.length === 0
                  ? 'All'
                  : selected.map(id => departments.find(d => d.id === id)?.name || id).join(', ')}
                MenuProps={MenuProps}
              >
                {departments.map(d => (
                  <MenuItem key={d.id} value={d.id}>
                    <Checkbox checked={selectedDepartments.includes(d.id)} size="small" />
                    <ListItemText primary={d.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Position Filter (Multi-select) */}
            <FormControl size="small" fullWidth>
              <InputLabel>Position</InputLabel>
              <Select
                multiple
                value={selectedPositions}
                onChange={(e) => setSelectedPositions(e.target.value)}
                input={<OutlinedInput label="Position" />}
                renderValue={(selected) => selected.length === 0
                  ? 'All'
                  : selected.map(id => positions.find(p => p.id === id)?.title || id).join(', ')}
                MenuProps={MenuProps}
              >
                {positions.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    <Checkbox checked={selectedPositions.includes(p.id)} size="small" />
                    <ListItemText primary={p.title} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Seniority Level Filter (Multi-select) */}
            <FormControl size="small" fullWidth>
              <InputLabel>Seniority Level</InputLabel>
              <Select
                multiple
                value={selectedSeniorityLevels}
                onChange={(e) => setSelectedSeniorityLevels(e.target.value)}
                input={<OutlinedInput label="Seniority Level" />}
                renderValue={(selected) => selected.length === 0
                  ? 'All'
                  : selected.map(id => seniorityLevels.find(l => l.id === id)?.name || id).join(', ')}
                MenuProps={MenuProps}
              >
                {seniorityLevels.map(l => (
                  <MenuItem key={l.id} value={l.id}>
                    <Checkbox checked={selectedSeniorityLevels.includes(l.id)} size="small" />
                    <ListItemText primary={l.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tenure Range Filter (Multi-select) */}
            <FormControl size="small" fullWidth>
              <InputLabel>Tenure</InputLabel>
              <Select
                multiple
                value={selectedTenureRanges}
                onChange={(e) => setSelectedTenureRanges(e.target.value)}
                input={<OutlinedInput label="Tenure" />}
                renderValue={(selected) => selected.length === 0
                  ? 'All'
                  : selected.map(v => TENURE_RANGES.find(r => r.value === v)?.label || v).join(', ')}
                MenuProps={MenuProps}
              >
                {TENURE_RANGES.filter(r => r.value !== 'all').map(r => (
                  <MenuItem key={r.value} value={r.value}>
                    <Checkbox checked={selectedTenureRanges.includes(r.value)} size="small" />
                    <ListItemText primary={r.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Hire Date From */}
            <DatePicker
              label="Hire Date From"
              value={hireDateFrom}
              onChange={setHireDateFrom}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />

            {/* Hire Date To */}
            <DatePicker
              label="Hire Date To"
              value={hireDateTo}
              onChange={setHireDateTo}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />

            {/* Clear Filters Button */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                disabled={!hasActiveFilters}
                fullWidth
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Collapse>

        {/* Table */}
        <Paper sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <TableContainer sx={{ flexGrow: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Seniority Level</TableCell>
                  <TableCell>Hire Date</TableCell>
                  <TableCell>Tenure</TableCell>
                  <TableCell>Status</TableCell>
                  {!hideActions && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={hideActions ? 8 : 9} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {hasActiveFilters ? 'No employees found matching your filters.' : 'No employees found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((employee) => (
                      <TableRow
                        key={employee.id}
                        hover
                        onClick={onRowClick ? () => onRowClick(employee) : undefined}
                        sx={onRowClick ? { cursor: 'pointer' } : undefined}
                      >
                        <TableCell>{getEmployeeFullName(employee) || 'N/A'}</TableCell>
                        <TableCell>{employee.email}</TableCell>
                        <TableCell>{employee.department?.name || 'N/A'}</TableCell>
                        <TableCell>{employee.position?.title || 'N/A'}</TableCell>
                        <TableCell>{employee.seniorityLevel?.name || 'N/A'}</TableCell>
                        <TableCell>{employee.hireDate ? new Date(employee.hireDate).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>{formatTenure(employee.hireDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={ALL_STATUSES.find(s => s.value === employee.status)?.label || employee.status}
                            size="small"
                            color={getStatusColor(employee.status)}
                          />
                        </TableCell>
                        {!hideActions && (
                          <TableCell>
                            <Tooltip title="Edit" placement="top">
                              <IconButton aria-label="edit employee" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/employees/${employee.id}/edit`); }}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete" placement="top">
                              <IconButton aria-label="delete employee" size="small" onClick={(e) => { e.stopPropagation(); handleDelete(employee.id); }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredEmployees.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default EmployeeList;